import React, { useEffect, useState } from "react";
import { HardDrive } from "lucide-react";
import { getMyStorageUsage } from "../lib/api";

function fmtBytes(b) {
    if (!b || b < 0) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * iter-114: "Storage used" row inside the user account dropdown menu.
 * Refetches every time the menu opens (via the ``visible`` prop) so
 * the numbers are always fresh without polling in the background.
 *
 * Renders nothing for users with no cap (unlimited) AND no usage — no
 * point cluttering the menu. Once the user actually has stored bytes
 * we show their usage even if unlimited so they can see what they've
 * uploaded.
 */
export default function StorageUsageRow({ visible }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!visible) return;
        let dead = false;
        getMyStorageUsage()
            .then((r) => { if (!dead) setData(r); })
            .catch(() => { if (!dead) setData(null); });
        return () => { dead = true; };
    }, [visible]);

    if (!data) return null;
    if (data.exempt) return null;

    const used = data.used_bytes || 0;
    const cap = data.cap_bytes || 0;
    // Skip rendering entirely if there's nothing to show.
    if (cap === 0 && used === 0) return null;

    const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    // Bar colour gets warmer as the user approaches the cap.
    const barColor =
        cap === 0
            ? "bg-[#00A3FF]"
            : pct >= 90
                ? "bg-rose-500"
                : pct >= 75
                    ? "bg-amber-400"
                    : "bg-emerald-400";
    const label =
        cap === 0
            ? `${fmtBytes(used)} used`
            : `${fmtBytes(used)} / ${fmtBytes(cap)}`;

    return (
        <div
            className="px-3 py-2 border-t border-[#2A3655] text-[11px]"
            data-testid="storage-usage-row"
        >
            <div className="flex items-center gap-1.5 text-slate-400 mb-1.5">
                <HardDrive className="w-3 h-3 text-[#00A3FF]" />
                <span className="uppercase tracking-wider font-bold">Storage</span>
                <span className="ml-auto text-slate-300 font-mono" data-testid="storage-usage-label">
                    {label}
                </span>
            </div>
            {cap > 0 && (
                <>
                    <div className="h-1.5 bg-[#1C253C] rounded-full overflow-hidden">
                        <div
                            className={`h-full ${barColor} transition-all`}
                            style={{ width: `${Math.max(2, pct)}%` }}
                            data-testid="storage-usage-bar"
                        />
                    </div>
                    {pct >= 90 && (
                        <div className="mt-1 text-rose-300 font-bold uppercase tracking-wider">
                            {pct >= 100 ? "Limit reached" : "Almost full"}
                        </div>
                    )}
                </>
            )}
            {cap === 0 && (
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">
                    Unlimited
                </div>
            )}
        </div>
    );
}
